import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  getActivityGroups,
  getBranchFiles,
  getLogs,
  searchActivityLogs,
  selectActivityGroups,
  selectActivityLinks,
  selectActivityHeadScopeErpRecords,
  selectActivityPartitionLogs,
  selectBranchFiles,
  selectIsLoadingActivityGroups,
  selectIsLoadingLogs,
  selectLogs,
  setActivityGroupsPayload,
  setBranchFiles,
  setIsLoadingActivityGroups,
  setLogs,
  setLogsChecked,
  setActivityLogsShowAllSelection,
  setActivityLogsLastSelectedScopeId,
  setActivityLogsSearchStatus,
  setActivityLogsSearchHasMore,
  resetActivityLogsSearchPaginator,
} from './redux';
import { toast } from 'utils/toast';
import { Branch, OrganizationData, selectIsSocketConnected, selectOrganizationData, setOrganizationData } from 'pages/redux';
import {
  LogGroups, LogKey, logGroupsRaw
} from './Logs';
import { selectPermission, selectToken } from 'redux/global';
import type { IResponse } from 'types';
import { selectErpRecords, fetchErpRecords } from 'pages/Records/redux';
import { canViewReportContextStandings } from 'pages/Settings/redux';
import { isReportContextStandingLogType } from 'pages/Reporting/reportContextUsers';
import { usePath } from 'pages/hooks';
import { useLocation } from 'react-router-dom';
import { store } from 'redux/store';
import { persistSharedActivityLogsPage } from 'utils/activityLogsMergedPool';
import {
  buildActivityLogsSearchPayload,
  type ActivityLogsSearchMode,
} from './activityLogsSearchPayload';
import { buildDefaultActivityLogsSearchCriteria } from './activityLogsSearchBaseline';
import { ACTIVITY_LOGS_PER_PAGE } from './activityLogsTableAmount';
import {
  buildInventoryGroupActivityLogs,
  buildInventoryItemActivityLogs,
  departmentsPresentInLogs,
  shortenBranchFileDetails,
} from './logUtils';
import { wrapLogActorMark, wrapLogBold, wrapLogDept, wrapLogDesc } from './logFormat';
import {
  ActivityGroupsModel,
  SHOW_ALL_ACTIVITY_LOGS,
  buildActivityGroupsModel,
  collectAllScopeOccurrenceKeys,
  selectionForShowAll,
  toggleGroupSelection,
  toggleGroupTypeSelection,
  toggleOccurrenceSelection,
} from './activityGroupsModel';
import {
  augmentActivityGroupsFromLogTrackers,
  mapLogsThroughTrackers,
} from './logTracking';

export type LogSku = {
  id: string,
  name: string,
  value?: string | number | null,
  quantity?: string | number | null,
  unit?: string | null,
};

export type Log = {
  id: string,
  /** Activity scope string (API maps group_type → type + groupType). */
  type: string,
  groupType?: string | null,
  message: string,
  date: string,
  value: string,
  updatedAsOf: string,
  isDelete?: string,
  /** Snapshot of the deleted log on 'Log deleted' rows (restore re-inserts from this). */
  logTable?: Log | null,
  /** Plural departments field from API. */
  departments?: string | null,
  /** Legacy singular — still accepted when reading older payloads. */
  department?: string | null,
  /** Inventory category for inventory-related cash / lifecycle events. */
  inventory_type?: string | null,
  /** Units sold or adjusted for inventory item activityLogs. */
  units?: string | number | null,
  /** Inventory item id when the activity (type) is an inventory item. */
  inventory_item_id?: string | null,
  /** Staff id from auth of the user who created this log. */
  created_by?: string | null,
  /** Booking id for stay-scoped activityLogs (e.g. Key card access). */
  booking_id?: string | null,
  /** Full booking snapshot at log time (nested so booking.id cannot overwrite log id). */
  booking?: Record<string, any> | null,
  /** Separate from activity scope (group type) — event classification. */
  eventType?: string | null,
  /** erp_records / custom master-data id stored in activity_logs.action_data_id. */
  action_data_id?: string | null,
  actionDataId?: string | null,
  /** Staff username or id of the reviewer on workflow operation logs. */
  reviewed_by?: string | null,
  reviewedBy?: string | null,
  /** approved | rejected | closed on workflow operation logs. */
  operation_status?: string | null,
  operationStatus?: string | null,
  /** People involved — always includes whoever logged the event. */
  concerned_parties?: string | null,
  concernedParties?: string | null,
  /** Manager report id when this row was written by filing that report. */
  managerReportId?: string | null,
  /** Full report-set snapshot for /logs full-screen filed preview. */
  reportFiledPreview?: {
    subject?: string,
    descriptionBlocks?: unknown,
    reportSections?: unknown,
    referencedActivityLogs?: unknown,
    metricsBySet?: unknown,
    showCharts?: boolean,
  } | null,
  /** Master-data / SKU trackers — charts and /logs read-side resolver. */
  tracking_items?: import('pages/Records/customMasterData').MasterDataLogTrackingItem[] | null,
  trackingItems?: import('pages/Records/customMasterData').MasterDataLogTrackingItem[] | null,
  /** SKUs attached to this log (id + name + value/quantity). */
  skus?: LogSku[] | null,
  /** Legacy path field — UI prefers groupType + skus. */
  relatedGroups?: { id: string, kind?: string }[] | null,
};

export const oneYearAgo = new Date();
oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

/** Branch-file rows merged into page-1 search — read from store so search callback stays stable. */
const branchFileLogsFromStore = (): Log[] => {
  const state = store.getState();
  const branchFiles = state.logs.branchFiles;
  const branches = state.views.organizationData?.branches || [];
  const addLogs: Log[] = [];
  branches.filter((b) => !b.isDeleted).forEach((branch) => {
    const dept = branch.name;
    if (!branchFiles?.[dept]) return;
    Object.entries(branchFiles[dept]).forEach(([sheetName, sheet]) => {
      sheet.filter((s) => +new Date(s.date) >= +oneYearAgo).forEach((l) => {
        const fileName = l.sheetName || sheetName;
        addLogs.push({
          id: `F_${dept}_${fileName}_${l.id}`,
          type: 'Document',
          message: `${wrapLogDept(dept)} imported a document file ${wrapLogDesc(shortenBranchFileDetails(l.message))} in ${wrapLogBold(fileName)} by ${wrapLogActorMark(l.updatedBy)}`,
          date: l.date,
          value: l.value.toString(),
          updatedAsOf: l.updatedAsOf,
          departments: dept,
          department: dept,
        });
      });
    });
  });
  return addLogs;
};

const actionsLogTypesForPermission = (
  permission: number | null | undefined,
): LogGroups['Actions'] => (
  canViewReportContextStandings(permission)
    ? [...logGroupsRaw.Actions]
    : logGroupsRaw.Actions.filter((type) => !isReportContextStandingLogType(type))
);

/** Compatibility shim: inventory/dept lists for search archives & Metrics — not left pane. */
const buildCompatLogGroups = (
  permission: number | null | undefined,
  organizationData: OrganizationData | null | undefined,
  logs: Log[] | null,
): {
  logGroups: LogGroups,
  deletedDepartmentNames: Set<string>,
  deletedInventoryItemNames: Set<string>,
  deletedInventoryGroupNames: Set<string>,
} => {
  const { branches } = organizationData || {};
  const logGroups: LogGroups = {
    ...logGroupsRaw,
    Departments: [...logGroupsRaw.Departments],
    'Department entries': [...logGroupsRaw['Department entries']],
    Inventory: [...logGroupsRaw.Inventory],
    'Inventory Group': [...logGroupsRaw['Inventory Group']],
    Actions: actionsLogTypesForPermission(permission),
  };

  const activeBranches = (branches || []).filter((b) => !b.isDeleted);
  const deletedBranches = (branches || []).filter((b) => b.isDeleted);
  const allBranchNames = (branches || []).map((b) => b.name);
  const departmentsInLogs = new Set(departmentsPresentInLogs(logs, allBranchNames));

  const deletedWithLogs = deletedBranches
    .map((b) => b.name)
    .filter((name) => departmentsInLogs.has(name));

  const deletedDepartmentNames = new Set(deletedWithLogs);
  logGroups.Departments = [
    ...activeBranches.map((b) => b.name),
    ...deletedWithLogs,
    ...logGroups.Departments,
  ];
  logGroups['Inventory Group'] = buildInventoryGroupActivityLogs({
    inventoryTypes: organizationData?.prefs?.inventoryTypes,
    inventory: organizationData?.inventory,
    logs,
  });
  logGroups.Inventory = buildInventoryItemActivityLogs({
    inventory: organizationData?.inventory,
    logs,
    inventoryGroupNames: logGroups['Inventory Group'],
  });

  const activeInventoryItemNames = new Set(
    (organizationData?.inventory || [])
      .map((item) => item?.name?.trim().toLowerCase())
      .filter(Boolean) as string[],
  );
  const activeInventoryGroupNames = new Set<string>();
  (organizationData?.prefs?.inventoryTypes || []).forEach((t) => {
    if (t?.trim()) activeInventoryGroupNames.add(t.trim().toLowerCase());
  });
  (organizationData?.inventory || []).forEach((item) => {
    if (item?.type?.trim()) activeInventoryGroupNames.add(item.type.trim().toLowerCase());
  });

  const deletedInventoryItemNames = new Set(
    logGroups.Inventory.filter((name) => !activeInventoryItemNames.has(name.toLowerCase())),
  );
  const deletedInventoryGroupNames = new Set(
    logGroups['Inventory Group'].filter(
      (name) => !activeInventoryGroupNames.has(name.toLowerCase()),
    ),
  );

  return {
    logGroups,
    deletedDepartmentNames,
    deletedInventoryItemNames,
    deletedInventoryGroupNames,
  };
};

/** Shared across useActivityGroups instances so we do not double-fetch. */
let activityGroupsLoadedOrgKey: string | null = null;
let activityGroupsFetchPromise: Promise<boolean> | null = null;
let activityGroupsFailureBackoffUntil = 0;
const ACTIVITY_GROUPS_FAILURE_BACKOFF_MS = 30_000;

/** Shared latest-page fetch — many useLogs() mounts must not each call searchlogs. */
let latestActivityLogsLoadedOrgKey: string | null = null;
let latestActivityLogsFetchPromise: Promise<IResponse> | null = null;
let activityLogsSearchBackoffUntil = 0;
const ACTIVITY_LOGS_SEARCH_BACKOFF_MS = 30_000;

export const invalidateLatestActivityLogsCache = () => {
  latestActivityLogsLoadedOrgKey = null;
  latestActivityLogsFetchPromise = null;
  activityLogsSearchBackoffUntil = 0;
};

/** Call after DB reseed so /logs picks up new scope ids. */
export const invalidateActivityGroupsCache = () => {
  activityGroupsLoadedOrgKey = null;
  activityGroupsFailureBackoffUntil = 0;
};

export const useActivityGroups = () => {
  const dispatch = useAppDispatch();
  const organizationData = useAppSelector(selectOrganizationData);
  const token = useAppSelector(selectToken);
  const isSocketConnected = useAppSelector(selectIsSocketConnected);
  const groups = useAppSelector(selectActivityGroups);
  const links = useAppSelector(selectActivityLinks);
  const isLoadingActivityGroups = useAppSelector(selectIsLoadingActivityGroups);

  const fetchActivityGroups = useCallback(async (force?: boolean) => {
    const orgId = organizationData?.id;
    if (!token || orgId == null || !isSocketConnected) return;
    const orgKey = String(orgId);

    if (Date.now() < activityGroupsFailureBackoffUntil) return;
    if (!force && activityGroupsLoadedOrgKey === orgKey) return;

    // Every useLogs() instance mounts together — share one in-flight request.
    if (activityGroupsFetchPromise) {
      await activityGroupsFetchPromise;
      if (!force && activityGroupsLoadedOrgKey === orgKey) return;
    }
    if (!force && activityGroupsLoadedOrgKey === orgKey) return;

    const promise = (async (): Promise<boolean> => {
      dispatch(setIsLoadingActivityGroups(true));
      try {
        const res = await dispatch(getActivityGroups(orgId));
        if (res.status === 'success') {
          const data = res.data || {};
          const nextGroups = Array.isArray(data.groups) ? data.groups : [];
          const nextLinks = Array.isArray(data.links) ? data.links : [];
          dispatch(setActivityGroupsPayload({
            groups: nextGroups,
            links: nextLinks,
            headScopeErpRecords: Array.isArray(data.headScopeErpRecords)
              ? data.headScopeErpRecords
              : [],
            partitionLogs: Array.isArray(data.partitionLogs) ? data.partitionLogs : [],
          }));
          activityGroupsLoadedOrgKey = orgKey;
          activityGroupsFailureBackoffUntil = 0;
          return true;
        }
        // Back off after 429/errors so failed mounts do not stampede the API.
        activityGroupsFailureBackoffUntil = Date.now() + ACTIVITY_GROUPS_FAILURE_BACKOFF_MS;
        dispatch(setActivityGroupsPayload({
          groups: [], links: [], headScopeErpRecords: [], partitionLogs: [],
        }));
        return false;
      } finally {
        dispatch(setIsLoadingActivityGroups(false));
        activityGroupsFetchPromise = null;
      }
    })();

    activityGroupsFetchPromise = promise;
    await promise;
  }, [dispatch, organizationData?.id, token, isSocketConnected]);

  useEffect(() => {
    if (!token || organizationData?.id == null || !isSocketConnected) return;
    void fetchActivityGroups();
  }, [token, organizationData?.id, isSocketConnected, fetchActivityGroups]);

  return {
    activityGroups: groups,
    activityLinks: links,
    isLoadingActivityGroups,
    fetchActivityGroups,
  };
};

export const useLogs = () => {
  const logs = useAppSelector(selectLogs);
  const isLoadingLogs = useAppSelector(selectIsLoadingLogs);
  const branchFiles = useAppSelector(selectBranchFiles);
  const organizationData = useAppSelector(selectOrganizationData);
  const permission = useAppSelector(selectPermission);
  const { branches } = organizationData || {};
  const {
    logGroups,
    deletedDepartmentNames,
    deletedInventoryItemNames,
    deletedInventoryGroupNames,
  } = useMemo(
    () => buildCompatLogGroups(permission, organizationData, logs),
    [permission, organizationData, logs],
  );

  const {
    activityGroups,
    activityLinks,
    isLoadingActivityGroups,
    fetchActivityGroups,
  } = useActivityGroups();
  const erpRecords = useAppSelector(selectErpRecords);
  const headScopeErpRecords = useAppSelector(selectActivityHeadScopeErpRecords);
  const partitionLogs = useAppSelector(selectActivityPartitionLogs);
  const dispatch = useAppDispatch();

  useEffect(() => {
    // VO leaves use action_data_id → Name(CODE) from erp_records; head-scope alone
    // is not enough (products/assets/services live outside org/location/dept).
    if (!erpRecords?.length) {
      void dispatch(fetchErpRecords());
    }
  }, [dispatch, erpRecords?.length]);

  const displayLogs = useMemo(
    () => mapLogsThroughTrackers(logs),
    [logs],
  );

  const augmentedActivityGroups = useMemo(
    () => augmentActivityGroupsFromLogTrackers(activityGroups, activityLinks, logs),
    [activityGroups, activityLinks, logs],
  );

  // Partitions use server partitionLogs + structural erp rows; table still uses client logs.
  const activityGroupsModel: ActivityGroupsModel = useMemo(
    () => buildActivityGroupsModel(augmentedActivityGroups.groups, {
      links: augmentedActivityGroups.links,
      logs: logs || [],
      partitionLogs: partitionLogs?.length ? partitionLogs : logs || [],
      erpRecords: erpRecords || [],
      headScopeErpRecords: headScopeErpRecords?.length ? headScopeErpRecords : erpRecords || [],
      inventory: organizationData?.inventory,
    }),
    [
      augmentedActivityGroups,
      logs,
      partitionLogs,
      erpRecords,
      headScopeErpRecords,
      organizationData?.inventory,
    ],
  );

  const getLogsFromBranchFiles = () => {
    const addLogs: Log[] = [];

    logGroups.Departments?.forEach((b) => {
      if (branchFiles?.[b]) {
        Object.entries(branchFiles[b]).forEach(([sheetName, sheet]) => {
          sheet.filter((s) => +new Date(s.date) >= +oneYearAgo).forEach((l) => {
            const fileName = l.sheetName || sheetName;
            const newLog: Log = {
              id: `F_${b}_${fileName}_${l.id}`,
              type: 'Document',
              message: `${wrapLogDept(b)} imported a document file ${wrapLogDesc(shortenBranchFileDetails(l.message))} in ${wrapLogBold(fileName)} by ${wrapLogActorMark(l.updatedBy)}`,
              date: l.date,
              value: l.value.toString(),
              updatedAsOf: l.updatedAsOf,
              departments: b,
              department: b,
            };
            addLogs.push(newLog);
          });
        });
      }
    });

    return [...addLogs];
  };

  const fetchLogs = async () => {
    const res = await dispatch(getLogs());

    if (res.status === 'success') {
      const branchFileLogs = getLogsFromBranchFiles();
      dispatch(setLogs([...res.data, ...branchFileLogs]));
      return;
    }
    toast(res.data, { type: 'error' });
  };

  const runActivityLogsSearch = useCallback(async (
    mode: ActivityLogsSearchMode = 'search',
    page = 1,
    opts?: { silent?: boolean, force?: boolean },
  ) => {
    const organizationId = store.getState().views?.organizationData?.id;
    const orgKey = organizationId != null && String(organizationId) !== ''
      ? String(organizationId)
      : '';
    const isLatestPageOne = mode === 'latest' && page === 1;

    if (isLatestPageOne && orgKey) {
      if (Date.now() < activityLogsSearchBackoffUntil) {
        const cached = store.getState().logs.logs || [];
        return {
          status: 'success',
          data: { logs: cached, hasMore: store.getState().logs.activityLogsSearchHasMore },
        } as IResponse;
      }
      if (!opts?.force && latestActivityLogsLoadedOrgKey === orgKey && (store.getState().logs.logs?.length || 0) > 0) {
        return {
          status: 'success',
          data: {
            logs: store.getState().logs.logs || [],
            hasMore: store.getState().logs.activityLogsSearchHasMore,
          },
        } as IResponse;
      }
      if (latestActivityLogsFetchPromise) {
        return latestActivityLogsFetchPromise;
      }
    }

    const executeSearch = async (): Promise<IResponse> => {
      const logsState = store.getState().logs;
      const activeChecked = logsState.checked || [];
      const payload = buildActivityLogsSearchPayload(buildDefaultActivityLogsSearchCriteria({
        logSearches: logsState.logSearches || [],
        logSearch: logsState.logSearch || '',
        logSearchOptions: logsState.logSearchOptions,
        checked: activeChecked,
        valueMode: logsState.logsValueMode === 'units' ? 'units' : 'value',
        allItems: logsState.activityLogsAllItems,
        cashIn: logsState.activityLogsCashIn,
        cashOut: logsState.activityLogsCashOut,
        useTimeRange: Boolean(
          logsState.activityLogsUseTimeRange
          && logsState.singleLogTime?.startDate
          && logsState.singleLogTime?.endDate,
        ),
        startDate: logsState.singleLogTime?.startDate,
        endDate: logsState.singleLogTime?.endDate,
        onlyLogIds: logsState.activityLogsOnlyLogIds,
        startLogId: logsState.activityLogsStartLogId,
        endLogId: logsState.activityLogsEndLogId,
        viewAs: logsState.activityLogsViewAs,
        associateElevatedActivityLogs: Boolean(logsState.associateElevatedActivityLogs),
        isComplianceActivity: Boolean(logsState.isComplianceActivity),
        complianceActivityStaffId: logsState.complianceActivityStaffId,
      }), mode, page);
      // Fresh Search / Latest — not paginator walking back to page 1 (silent).
      if (page <= 1 && !opts?.silent) {
        dispatch(resetActivityLogsSearchPaginator());
      }
      if (!opts?.silent) {
        dispatch(setActivityLogsSearchStatus('loading'));
      }
      const res = await dispatch(searchActivityLogs(payload));
      if (res.status === 'success') {
        const body = res.data && !Array.isArray(res.data)
          ? res.data as { logs?: Log[], hasMore?: boolean }
          : { logs: res.data || [], hasMore: (res.data || []).length >= ACTIVITY_LOGS_PER_PAGE };
        const apiRows = body.logs || [];
        // Empty later page — keep current rows and tell the paginator to stay.
        if (page > 1 && apiRows.length === 0) {
          dispatch(setActivityLogsSearchHasMore(false));
          dispatch(setActivityLogsSearchStatus('results'));
          return { ...res, status: 'success', data: { logs: apiRows, hasMore: false } } as IResponse;
        }
        const branchFileLogs = page <= 1 ? branchFileLogsFromStore() : [];
        const mergedRows = [...apiRows, ...branchFileLogs];
        // Do not wipe Command Center charts with an empty latest page mid-race.
        const prevLogs = store.getState().logs.logs;
        if (
          isLatestPageOne
          && mergedRows.length === 0
          && (prevLogs?.length || 0) > 0
        ) {
          dispatch(setActivityLogsSearchStatus('results'));
          return {
            ...res,
            status: 'success',
            data: { logs: prevLogs, hasMore: store.getState().logs.activityLogsSearchHasMore },
          } as IResponse;
        }
        dispatch(setLogs(mergedRows));
        dispatch(setActivityLogsSearchHasMore(Boolean(body.hasMore)));
        dispatch(setActivityLogsSearchStatus('results'));
        if (organizationId != null && organizationId !== '') {
          persistSharedActivityLogsPage(String(organizationId), page, {
            logs: mergedRows,
            hasMore: Boolean(body.hasMore),
          });
        }
        if (isLatestPageOne && orgKey) {
          latestActivityLogsLoadedOrgKey = orgKey;
          activityLogsSearchBackoffUntil = 0;
        }
        return res;
      }
      if (isLatestPageOne && orgKey) {
        activityLogsSearchBackoffUntil = Date.now() + ACTIVITY_LOGS_SEARCH_BACKOFF_MS;
      }
      if (!opts?.silent) {
        dispatch(setActivityLogsSearchStatus('idle'));
      }
      toast(res.data, { type: 'error' });
      return res;
    };

    if (isLatestPageOne && orgKey) {
      const promise = executeSearch();
      latestActivityLogsFetchPromise = promise;
      try {
        return await promise;
      } finally {
        latestActivityLogsFetchPromise = null;
      }
    }

    return executeSearch();
  }, [dispatch]);

  const fetchBranchFiles = async (noToast?: boolean) => {
    const res = await dispatch(getBranchFiles());

    if (res.status === 'success') {
      dispatch(setBranchFiles(res.data.branchFiles));
      return;
    }

    if (!noToast) {
      toast(res.data, { type: 'error' });
    }
  };

  const deleteLocalLog = (id: string | number) => {
    // Read from the store so a concurrent get_added_log (e.g. "Log deleted")
    // is not wiped by filtering a stale hook snapshot.
    const { logs: currentLogs } = store.getState().logs;
    const newLogs = (currentLogs || []).filter((l) => l.id.toString() !== id.toString());
    dispatch(setLogs(newLogs));
  };

  const deleteLocalLogType = (type: string) => {
    const { logs: currentLogs } = store.getState().logs;
    const newLogs = (currentLogs || []).filter((l) => l.type !== type);
    dispatch(setLogs([...newLogs]));
  };

  const updateLocalLog = (log: Log) => {
    const { logs: currentLogs } = store.getState().logs;
    const newLogs = [...(currentLogs || []).filter((l: Log) => l.id.toString() !== log.id.toString()),
      log];
    dispatch(setLogs([...newLogs]));
  };

  const updateLocalBranch = (branch: Branch, previousName?: string) => {
    const removeNames = new Set(
      [branch?.name, previousName].filter(Boolean) as string[],
    );
    const newBranches = [
      ...(branches || []).filter((b) => !removeNames.has(b.name)),
      branch,
    ];
    if (organizationData) {
      dispatch(setOrganizationData({ ...organizationData, branches: newBranches }));
    }
  };

  const deleteLocalBranch = (branch: string) => {
    const existing = (branches || []).find((b) => b.name === branch);
    if (!existing || !organizationData) return;
    const newBranches = (branches || []).map((b) =>
      (b.name === branch ? { ...b, staff: [], isDeleted: true } : b));
    dispatch(setOrganizationData({ ...organizationData, branches: newBranches }));
  };

  const deleteLocalBranchFile = (branch: string, sheetName: string) => {
    const newBranchFiles = { ...branchFiles?.[branch] || {} };
    delete newBranchFiles[sheetName];
    dispatch(setBranchFiles({ ...branchFiles, [branch]: newBranchFiles }));
  };

  const deleteLocalBranchFiles = (branch: string) => {
    const newBranchFiles = { ...branchFiles };
    delete newBranchFiles[branch];
    dispatch(setBranchFiles({ ...newBranchFiles }));
  };

  return {
    isLoadingLogs,
    logs: displayLogs,
    rawLogs: logs,
    logGroups,
    deletedDepartmentNames,
    deletedInventoryItemNames,
    deletedInventoryGroupNames,
    activityGroups,
    activityLinks,
    activityGroupsModel,
    isLoadingActivityGroups,
    fetchActivityGroups,
    deleteLocalLog,
    deleteLocalLogType,
    updateLocalLog,
    getLogsFromBranchFiles,
    fetchBranchFiles,
    fetchLogs,
    runActivityLogsSearch,
    deleteLocalBranchFile,
    deleteLocalBranchFiles,
    updateLocalBranch,
    deleteLocalBranch
  };
};

export const useFetchLogs = () => {
  const logs = useAppSelector(selectLogs);
  const branchFiles = useAppSelector(selectBranchFiles);
  const organizationData = useAppSelector(selectOrganizationData);
  const token = useAppSelector(selectToken);
  const { isHomePath } = usePath();
  const dispatch = useAppDispatch();
  const {
    fetchBranchFiles,
    getLogsFromBranchFiles,
  } = useLogs();
  const location = useLocation();
  const logsRef = useRef(logs);

  useEffect(() => {
    // Do not clear isLoadingLogs here — Navigation's metric aggregation owns
    // that flag so /metrics waits until buckets are ready (avoids tab freeze).
    logsRef.current = logs;
  }, [logs]);

  useEffect(() => {
    if (!branchFiles || !logsRef.current) return;
    const baseLogs = (logsRef.current || []).filter(
      (l) => !l.id.toString().includes('F_'),
    );
    const logsFromBranchFiles = getLogsFromBranchFiles();
    const prevFileCount = (logsRef.current || []).filter(
      (l) => l.id.toString().includes('F_'),
    ).length;
    // Skip no-op merges so metrics/logs pages do not remount for free.
    if (prevFileCount === logsFromBranchFiles.length && logsFromBranchFiles.length === 0) {
      return;
    }
    dispatch(setLogs([...baseLogs, ...logsFromBranchFiles]));
  }, [branchFiles]);

  useEffect(() => {
    if (!token || !organizationData?.id || isHomePath) return;

    // Activity logs load from /logs Search — not at app boot.
    if (branchFiles == null) {
      (async () => {
        await fetchBranchFiles();
      })();
    }
  }, [organizationData, location, token, branchFiles, isHomePath]);
};

export const useLogsSelector = ({
  triggerAllItems0,
  triggerAllItems1,
}:{
  triggerAllItems0?: Function,
  triggerAllItems1?: Function,
}) => {
  const dispatch = useAppDispatch();
  const { activityGroupsModel, logGroups } = useLogs();

  const onClickShowAll = (checked1: string[], paneOnly?: boolean) => {
    const next = checked1.includes(SHOW_ALL_ACTIVITY_LOGS) ? [] : selectionForShowAll();
    if (paneOnly) {
      dispatch(setLogsChecked(next));
    } else {
      dispatch(setActivityLogsShowAllSelection(next));
    }
    dispatch(setActivityLogsLastSelectedScopeId(null));
  };

  /** Activity-scope header: cascade all descendant leaf activity-scope ids. */
  const onClickActivityGroup = (groupId: string, checked1: string[]) => {
    const next = toggleGroupSelection(groupId, checked1, activityGroupsModel);
    dispatch(setLogsChecked(next));
    const withoutShowAll = checked1.filter((c) => c !== SHOW_ALL_ACTIVITY_LOGS);
    if (next.length > withoutShowAll.length) {
      triggerAllItems0?.();
    }
    // Show all → click is a deselect of that branch, not a last pick.
    if (!checked1.includes(SHOW_ALL_ACTIVITY_LOGS) && next.length > withoutShowAll.length) {
      dispatch(setActivityLogsLastSelectedScopeId(groupId));
    }
  };

  /** Single leaf activity-scope row. */
  const onClickActivityGroupType = (groupTypeId: string, checked1: string[]) => {
    const next = toggleGroupTypeSelection(groupTypeId, checked1, activityGroupsModel);
    dispatch(setLogsChecked(next));
    if (next.includes(groupTypeId) && !checked1.includes(groupTypeId)) {
      dispatch(setActivityLogsLastSelectedScopeId(groupTypeId));
      triggerAllItems1?.('Inventory Group', groupTypeId);
    }
  };

  /** Tree header / leaf: this occurrence only (shared children stay independent). */
  const onClickActivitySkuIds = (
    skuIds: string[],
    checked1: string[],
    lastSelectedScopeId?: string,
  ) => {
    const expandKeys = collectAllScopeOccurrenceKeys(activityGroupsModel);
    const next = toggleOccurrenceSelection(skuIds, checked1, activityGroupsModel, expandKeys);
    console.log(checked1, activityGroupsModel, next)
    dispatch(setLogsChecked(next));
    const wasOn = checked1.includes(SHOW_ALL_ACTIVITY_LOGS)
      || (skuIds.length > 0 && skuIds.every((id) => checked1.includes(id)));
    if (!wasOn) {
      dispatch(setActivityLogsLastSelectedScopeId(lastSelectedScopeId || skuIds[0] || null));
      triggerAllItems0?.();
    }
  };

  // Thin legacy wrappers so QuickOptions / Metrics compile until deep-links migrate.
  const onClickActivityKey = (key: LogKey, checked1: string[]) => {
    const value = (logGroups as any)[key] as string[] | undefined;
    if (!value) return;
    const allChildrenSelected = value.length > 0
      && value.every((t) => checked1.includes(t));
    if (checked1.includes(key) || allChildrenSelected) {
      const newC = checked1.filter((c) => ![...value, key, 'Show all'].includes(c));
      dispatch(setLogsChecked(newC));
    } else {
      const withoutGroup = checked1.filter(
        (c) => c !== key && c !== 'Show all' && !value.includes(c),
      );
      dispatch(setLogsChecked([...withoutGroup, ...value, key]));
      triggerAllItems0?.();
    }
  };

  const onClickActivity = (key: LogKey, logType: string, checked1: string[]) => {
    if (checked1.includes(logType)) {
      const newC = checked1.filter((c) => c !== 'Show all' && c !== key && c !== logType);
      dispatch(setLogsChecked(newC));
    } else {
      const newC = [...checked1, logType];
      dispatch(setLogsChecked(newC));
      triggerAllItems1?.(key, logType);
    }
  };

  return {
    onClickShowAll,
    onClickActivityGroup,
    onClickActivityGroupType,
    onClickActivitySkuIds,
    onClickActivityKey,
    onClickActivity,
  };
};

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState<Record<string, undefined | number>>({
    width: 278,
    height: undefined,
  });
  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return windowSize;
};
